import { useEffect } from 'react'
import { useForm, Controller, useFieldArray, useWatch, type Control, type FieldErrors, type UseFormSetValue } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Alert, CircularProgress, IconButton, Autocomplete, Box,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createPurchaseRequest, updatePurchaseRequest, getPurchaseRequest,
  type PurchaseRequestListItem, type SavePurchaseRequestPayload,
} from '../../api/achizitiiCereri'
import { listArticles, listUnits, type ArticleListItem, type UnitOfMeasure } from '../../api/nomenclatoare'
import { listSuppliers, type SupplierListItem } from '../../api/furnizori'
import { errorMessage } from '../../api/client'

const lineSchema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  description: z.string().optional(),
  quantityRequested: z.string().min(1, 'Câmp obligatoriu'),
  unitOfMeasureId: z.string().min(1, 'Selectați o UM'),
  suggestedSupplierId: z.string().nullable(),
})
const schema = z.object({
  requestedDate: z.string().min(1, 'Câmp obligatoriu'),
  department: z.string().optional(),
  notes: z.string().optional(),
  estimatedTotal: z.string().min(1, 'Câmp obligatoriu'),
  currency: z.string().min(1, 'Câmp obligatoriu'),
  lines: z.array(lineSchema).min(1, 'Adăugați cel puțin o linie'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  requestedDate: new Date().toISOString().slice(0, 10), department: '', notes: '',
  estimatedTotal: '', currency: 'RON', lines: [],
}

/** FR-ACA-003 — linii propuse dintr-o lipsă deja identificată (fezabilitate BOM sau stoc sub minim), fără reintroducere de date. */
export interface PurchaseRequestPrefill {
  notes: string
  lines: { articleId: string; description: string; quantityRequested: string; unitOfMeasureId: string }[]
}

export default function PurchaseRequestFormDialog({ target, prefill, onClose }: {
  target: PurchaseRequestListItem | null | 'new'; prefill?: PurchaseRequestPrefill | null; onClose: () => void
}) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })
  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['achizitii', 'cereri', editing ? (target as PurchaseRequestListItem).id : null],
    queryFn: () => getPurchaseRequest((target as PurchaseRequestListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const lines = useFieldArray({ control, name: 'lines' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        requestedDate: detail.requestedDate, department: detail.department ?? '', notes: detail.notes ?? '',
        estimatedTotal: String(detail.estimatedTotal), currency: detail.currency,
        lines: detail.lines.map((l) => ({
          articleId: l.articleId, description: l.description ?? '', quantityRequested: String(l.quantityRequested),
          unitOfMeasureId: l.unitOfMeasureId, suggestedSupplierId: l.suggestedSupplierId,
        })),
      })
    } else if (target === 'new') {
      reset(prefill
        ? { ...EMPTY, notes: prefill.notes, lines: prefill.lines.map((l) => ({ ...l, suggestedSupplierId: null })) }
        : EMPTY)
    }
  }, [target, editing, detail, prefill, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SavePurchaseRequestPayload = {
        requestedDate: values.requestedDate, department: values.department || null, notes: values.notes || null,
        estimatedTotal: Number(values.estimatedTotal), currency: values.currency,
        lines: values.lines.map((l) => ({
          articleId: l.articleId, description: l.description || null, quantityRequested: Number(l.quantityRequested),
          unitOfMeasureId: l.unitOfMeasureId, suggestedSupplierId: l.suggestedSupplierId || null,
        })),
      }
      if (editing) return updatePurchaseRequest((target as PurchaseRequestListItem).id, payload)
      return createPurchaseRequest(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'cereri'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare cerere de achiziție' : 'Cerere de achiziție nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="requestedDate" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Data cererii" sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.requestedDate} helperText={errors.requestedDate?.message} />
              )} />
              <Controller name="department" control={control} render={({ field }) => (
                <TextField {...field} label="Departament (opțional)" sx={{ flexGrow: 1 }} />
              )} />
              <Controller name="estimatedTotal" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Total estimat" sx={{ width: 160 }}
                  error={!!errors.estimatedTotal} helperText={errors.estimatedTotal?.message} />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 100 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
            </Stack>
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} label="Note" fullWidth multiline minRows={2} />
            )} />

            {typeof errors.lines?.message === 'string' && <Alert severity="warning">{errors.lines.message}</Alert>}
            {lines.fields.map((f, i) => (
              <RequestLineRow
                key={f.id} control={control} index={i} errors={errors} setValue={setValue}
                articles={articles} units={units} fallbackSuppliers={suppliers}
                onRemove={() => lines.remove(i)}
              />
            ))}
            <Box>
              <Button size="small" startIcon={<Add />}
                onClick={() => lines.append({ articleId: '', description: '', quantityRequested: '', unitOfMeasureId: '', suggestedSupplierId: null })}>
                Adaugă linie
              </Button>
            </Box>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/**
 * Subcomponent per linie — necesar (nu doar stilistic) pentru ca fiecare linie să-și
 * poată urmări propriul articol selectat și să interogheze separat furnizorii care îl
 * livrează (tiparul `ZoneRow` de la `WarehouseFormDialog`: hook-urile React nu pot fi
 * apelate direct într-un `.map()`). Cât timp nu e ales niciun articol pe linie, dropdown-ul
 * de furnizor arată lista completă (`fallbackSuppliers`); după alegerea articolului, se
 * filtrează strict la furnizorii cu `SupplierArticle` pentru acel articol.
 */
function RequestLineRow({ control, index, errors, setValue, articles, units, fallbackSuppliers, onRemove }: {
  control: Control<FormValues>; index: number; errors: FieldErrors<FormValues>; setValue: UseFormSetValue<FormValues>
  articles?: ArticleListItem[]; units?: UnitOfMeasure[]; fallbackSuppliers?: SupplierListItem[]
  onRemove: () => void
}) {
  const articleId = useWatch({ control, name: `lines.${index}.articleId` })

  const { data: suppliersForArticle, isFetching: loadingSuppliers } = useQuery({
    queryKey: ['achizitii', 'furnizori', 'pentru-articol', articleId || null],
    queryFn: () => listSuppliers(undefined, articleId),
    enabled: !!articleId,
  })
  const supplierOptions = articleId ? (suppliersForArticle ?? []) : (fallbackSuppliers ?? [])

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Controller name={`lines.${index}.articleId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
          getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
          value={field.value || null}
          onChange={(_, v) => {
            field.onChange(v ?? '')
            // Furnizorul ales anterior poate să nu mai livreze noul articol — nu păstrăm o valoare devenită invizibilă în lista filtrată.
            setValue(`lines.${index}.suggestedSupplierId`, null)
          }}
          renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.lines?.[index]?.articleId} helperText={errors.lines?.[index]?.articleId?.message} />}
        />
      )} />
      <Controller name={`lines.${index}.quantityRequested`} control={control} render={({ field }) => (
        <TextField {...field} type="number" label="Cantitate" sx={{ width: 130 }}
          error={!!errors.lines?.[index]?.quantityRequested} helperText={errors.lines?.[index]?.quantityRequested?.message} />
      )} />
      <Controller name={`lines.${index}.unitOfMeasureId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ width: 130 }} options={units?.map((u) => u.id) ?? []}
          getOptionLabel={(id) => units?.find((u) => u.id === id)?.code ?? id}
          value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
          renderInput={(params) => <TextField {...params} label="UM" error={!!errors.lines?.[index]?.unitOfMeasureId} helperText={errors.lines?.[index]?.unitOfMeasureId?.message} />}
        />
      )} />
      <Controller name={`lines.${index}.suggestedSupplierId`} control={control} render={({ field }) => (
        <Autocomplete
          sx={{ width: 220 }} options={supplierOptions.map((s) => s.id)} loading={loadingSuppliers}
          getOptionLabel={(id) => supplierOptions.find((s) => s.id === id)?.name ?? id}
          value={field.value} onChange={(_, v) => field.onChange(v)}
          noOptionsText={articleId ? 'Niciun furnizor mapat pe acest articol' : 'Selectați întâi un articol'}
          renderInput={(params) => <TextField {...params} label="Furnizor sugerat" helperText={articleId ? 'Filtrat pe articolul selectat' : undefined} />}
        />
      )} />
      <IconButton onClick={onRemove} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
    </Stack>
  )
}
